import React, { Suspense, useCallback, useEffect } from 'react';

import {
  CloseOutlined,
  ExclamationCircleOutlined,
  LoginOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import BookOutlined from '@ant-design/icons/BookOutlined';
import ContactsOutlined from '@ant-design/icons/ContactsOutlined';
import EuroCircleOutlined from '@ant-design/icons/EuroCircleOutlined';
import GlobalOutlined from '@ant-design/icons/GlobalOutlined';
import HeartOutlined from '@ant-design/icons/HeartOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import MenuOutlined from '@ant-design/icons/MenuOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import NotificationOutlined from '@ant-design/icons/NotificationOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import SecurityScanOutlined from '@ant-design/icons/SecurityScanOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import { Badge, Button, ConfigProvider } from 'antd';
import { App as AntdApp } from 'antd';
import cn from 'classnames';
import { Provider } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { ABOUT_ROUTE, DASHBOARD_ROUTE, FAQ_ROUTE, PRICING_ROUTE } from 'routes/dashboard/list';
import { CREATE_PROPERTY_FORM_ROUTE } from 'routes/property/list';
import { LOGIN_ROUTE, PROFILE_ROUTE } from 'routes/user-management/list';
import { getIsLoggedIn, removeTokenCookies } from 'tools/cookieTools';
import { useQuery } from 'tools/hooks';
import {
  //  Dropdown,
  Icon,
  Typography,
} from 'ui';

import { MY_SERVICES_ROUTE } from 'routes/services/list';
import { watchedPropertiesSelector } from 'selectors/propertySlice.selector';
import { isOpenedSiderSelector } from 'selectors/systemSlice.selector';
import { ProfileSelector, isLoggedInSelector } from 'selectors/userSlice.selectors';
import { setActiveProperty, setTerms } from 'slices/propertySlice';
import { setOpenedSider, setServiceViewType } from 'slices/systemSlice';
import { logout } from 'slices/userSlice';
import { persistor, store, useAppDispatch, useAppSelector } from 'store';

import Footer from 'containers/footer';
import NavBar from 'containers/nav-bar';
import Filters from 'containers/nav-bar/filters';

import { USER_TYPE_ENUM } from 'types/core/userTypes';

import s from './App.module.scss';
import Pages from './pages';

const Analytics = React.lazy(() => import('./analytics'));

// const DROPDOWN_ITEMS = () => [
//   { key: 'post', label: <div className={s.dropdownItem}>List Property</div> },
//   { key: 'profile', label: <div className={s.dropdownItem}>Profile</div> },
// ];

// const GUEST_DROPDOWN_ITEMS = () => [
//   { key: 'post', label: <div className={s.dropdownItem}>List Property</div> },
//   { key: 'login', label: <div className={s.dropdownItem}>Log in</div> },
// ];

function App() {
  const { notification } = AntdApp.useApp();
  const navigate = useNavigate();
  const currentUser = useAppSelector(ProfileSelector);
  const isAuth = getIsLoggedIn();
  const isLogged = useAppSelector(isLoggedInSelector);
  const initiallyLogged = isLogged || isAuth;
  const dispatch = useAppDispatch();
  const isOpenedSidebar = useAppSelector(isOpenedSiderSelector);
  const query = useQuery();
  const step = query.get('step');
  const watchedProperties = useAppSelector(watchedPropertiesSelector);
  // const [openedDropdown, setOpenedDropdown] = useState(false);
  // const profile = useAppSelector(ProfileSelector);

  const handleClick = useCallback(() => {
    dispatch(setOpenedSider(!isOpenedSidebar));
  }, [isOpenedSidebar, dispatch]);

  const keyPressHandler = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      return;
    }
  };

  const goToFavourites = useCallback(() => {
    if (initiallyLogged) {
      navigate('profile?step=favourites');
    } else {
      notification.info({
        icon: <WarningOutlined />,
        message: 'You need to log in.',
        placement: 'topRight',
      });
      navigate(DASHBOARD_ROUTE);
    }
  }, [navigate, notification, initiallyLogged]);

  const goToDashboard = useCallback(() => {
    dispatch(setOpenedSider(false));
    navigate('/');
  }, [navigate, dispatch]);

  const goToServices = useCallback(() => {
    dispatch(setOpenedSider(false));
    navigate(MY_SERVICES_ROUTE);
  }, [navigate, dispatch]);

  const handleGoToAbout = useCallback(() => {
    dispatch(setOpenedSider(false));
    navigate(ABOUT_ROUTE);
  }, [navigate, dispatch]);

  const handleGoToFaq = useCallback(() => {
    dispatch(setOpenedSider(false));
    navigate(FAQ_ROUTE);
  }, [navigate, dispatch]);

  const handleGoToPricing = useCallback(() => {
    dispatch(setOpenedSider(false));
    navigate(PRICING_ROUTE);
  }, [navigate, dispatch]);

  const handleChangeStep = useCallback(
    (step: string) => {
      dispatch(setOpenedSider(false));
      navigate(`${PROFILE_ROUTE}?step=${step}`);
    },
    [dispatch, navigate],
  );

  const handleGoToLogin = useCallback(() => {
    dispatch(setOpenedSider(false));
    navigate(LOGIN_ROUTE);
  }, [navigate, dispatch]);

  useEffect(() => {
    if (isOpenedSidebar) {
      document.body.style.overflow = 'hidden'; // Disable scroll
    } else {
      document.body.style.overflow = ''; // Restore scroll
    }

    // Clean up on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpenedSidebar]);

  const handleLogout = useCallback(() => {
    dispatch(setTerms(undefined));
    dispatch(logout());
    dispatch(setOpenedSider(false));
    if (removeTokenCookies()) {
      window.location.reload();
      navigate('/');
    }
  }, [dispatch, navigate]);

  // const handleGoToProfile = useCallback(() => {
  //   dispatch(setOpenedSider(false));
  //   navigate(`${PROFILE_ROUTE}?step=userProfile`);
  // }, [navigate, dispatch]);

  const goToProperty = useCallback(() => {
    dispatch(setOpenedSider(false));
    if (initiallyLogged) {
      dispatch(setActiveProperty(undefined));
      navigate(CREATE_PROPERTY_FORM_ROUTE);
    } else {
      notification.info({
        icon: <WarningOutlined style={{ color: '#8E53E8' }} />,
        message: 'You need to log in before listing a property.',
        placement: 'topRight',
      });
      navigate(DASHBOARD_ROUTE);
    }
  }, [navigate, notification, initiallyLogged, dispatch]);

  // const handleGoToDashboard = useCallback(() => {
  //   dispatch(setOpenedSider(false));
  //   navigate(`${DASHBOARD_ROUTE}`);
  // }, [navigate, dispatch]);

  // const handleItemClick = useCallback(
  //   (ev: { key: string }) => {
  //     if (ev.key === 'post') {
  //       goToProperty();
  //     } else if (ev.key === 'profile') {
  //       handleGoToProfile();
  //     } else if (ev.key === 'login') {
  //       handleGoToDashboard();
  //     }
  //   },
  //   [goToProperty, handleGoToDashboard, handleGoToProfile],
  // );

  const slocation = useLocation();

  useEffect(() => {
    if (slocation.pathname !== '/service') {
      dispatch(setServiceViewType('list'));
    }
  }, [dispatch, slocation.pathname]);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#8E53E8',
              borderRadius: 8,
              colorTextBase: '#000000',
              colorLink: '#8E53E8',
              colorLinkHover: '#B989FF',
              colorLinkActive: '#000000',
            },
          }}>
          <div className="App">
            <div>
              <div className={s.content}>
                {(location.pathname === '/' ||
                  location.pathname.startsWith('/listings/detail/') ||
                  location.pathname.startsWith('/development/') ||
                  location.pathname.startsWith('/about') ||
                  location.pathname.startsWith('/pricing') ||
                  location.pathname.startsWith('/faq') ||
                  location.pathname.startsWith('/profile') ||
                  location.pathname.startsWith('/services') ||
                  location.pathname.startsWith('/listings')) && (
                  <div className={cn(s.mobileHeader)}>
                    <div className={s.logo}>
                      <Button style={{ paddingLeft: 0 }} type="link" onClick={goToDashboard}>
                        <Icon name="logo" width={142} height={20} />
                      </Button>
                    </div>
                    {/* <Dropdown
                      items={initiallyLogged === true ? DROPDOWN_ITEMS() : GUEST_DROPDOWN_ITEMS()}
                      placement="bottomRight"
                      open={openedDropdown}
                      onOpenChange={setOpenedDropdown}
                      handleItemClick={handleItemClick}>
                      {initiallyLogged ? (
                        <div className={s.profile}>
                          {profile?.profileImageURL &&
                            profile?.profileImageURL !==
                              'https://onthatstreet.blob.core.windows.net/content/users/profilepictures/' && (
                              <div className={s.uploadedFile}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <img
                                    className={s.uploadedImage}
                                    src={profile?.profileImageURL}
                                    width={28}
                                    height={28}
                                    alt="promotionMedia"
                                  />
                                </div>
                              </div>
                            )}
                          {(!profile?.profileImageURL ||
                            profile.profileImageURL ===
                              'https://onthatstreet.blob.core.windows.net/content/users/profilepictures/') && (
                            <div className={s.uploadedFile}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <img
                                  className={s.uploadedImage}
                                  src="/assets/images/avatar/avatar.svg"
                                  width={28}
                                  height={28}
                                  alt="promotionMedia"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={s.profile}>
                          <div className={s.uploadedFile}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                className={s.uploadedImage}
                                src="/assets/images/avatar/avatar.svg"
                                width={28}
                                height={28}
                                alt="promotionMedia"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </Dropdown> */}
                    {initiallyLogged && (
                      <Button
                        onClick={goToFavourites}
                        style={{ color: '#8E53E8', display: 'flex', alignItems: 'center', gap: 2 }}
                        type="link">
                        <Typography type="h6" weight={700}>
                          {watchedProperties?.length}
                        </Typography>
                        <Icon name="heartFilled" size={20} color="#8E53E8" />
                      </Button>
                    )}
                    <button className={s.menu} onClick={handleClick}>
                      <MenuOutlined />
                    </button>
                  </div>
                )}
                <Suspense fallback={null}>
                  <Analytics />
                </Suspense>
                <Pages />
                {((initiallyLogged && location.pathname !== '/') ||
                  location.pathname.startsWith('/listings/detail/') ||
                  location.pathname.startsWith('/development/') ||
                  location.pathname.startsWith('/about') ||
                  location.pathname.startsWith('/pricing') ||
                  location.pathname.startsWith('/faq')) && <Footer />}
              </div>

              <>
                {(initiallyLogged ||
                  location.pathname === '/' ||
                  location.pathname.startsWith('/listings/detail/') ||
                  location.pathname.startsWith('/development/') ||
                  location.pathname.startsWith('/about') ||
                  location.pathname.startsWith('/pricing') ||
                  location.pathname.startsWith('/services') ||
                  location.pathname.startsWith('/faq')) && (
                  <div className={s.navBar}>
                    <NavBar initiallyLogged={initiallyLogged} />
                  </div>
                )}
                {location.pathname === '/' && (
                  <div className={s.filterWrapper}>
                    <Filters />
                  </div>
                )}
                {isOpenedSidebar && (
                  <>
                    <div
                      className={s.shadow}
                      role="button"
                      tabIndex={0}
                      onKeyPress={keyPressHandler}
                      onClick={() => {
                        dispatch(setOpenedSider(false));
                      }}></div>
                  </>
                )}
                <div className={cn(s.mobileMenu, { [s.opened]: isOpenedSidebar })}>
                  <div className={s.profileMenuWrapper}>
                    <div
                      className={s.mobileHeader}
                      style={{
                        marginBottom: 8,
                        marginLeft: 4,
                        marginRight: 'auto',
                      }}>
                      <div className={s.logo}>
                        <Button style={{ paddingLeft: 0 }} type="link" onClick={goToDashboard}>
                          <Icon name="logo" width={142} height={20} />
                        </Button>
                      </div>
                      <button className={s.menu} onClick={handleClick}>
                        <CloseOutlined />
                      </button>
                    </div>
                    <div className={s.leftMenu}>
                      <ul style={{ listStyle: 'none' }}>
                        <li className={s.profileMenuItem}>
                          <Button
                            type="link"
                            onClick={goToDashboard}
                            className={`${s.menuItem} ${
                              location.pathname === '/buy' ? s.active : ''
                            }`}>
                            <GlobalOutlined />
                            Map
                          </Button>
                        </li>
                        <li className={s.profileMenuItem}>
                          <Button
                            type="link"
                            onClick={goToServices}
                            className={`${s.menuItem} ${
                              location.pathname === '/buy' ? s.active : ''
                            }`}>
                            <ContactsOutlined />
                            Services
                          </Button>
                        </li>
                        <li className={s.profileMenuItem}>
                          <Button
                            type="link"
                            onClick={handleGoToAbout}
                            className={`${s.menuItem} ${
                              location.pathname === '/about' ? s.active : ''
                            }`}>
                            <ExclamationCircleOutlined />
                            About
                          </Button>
                        </li>
                        <li className={s.profileMenuItem}>
                          <Button
                            type="link"
                            onClick={handleGoToFaq}
                            className={`${s.menuItem} ${
                              location.pathname === '/faq' ? s.active : ''
                            }`}>
                            <QuestionCircleOutlined />
                            FAQ
                          </Button>
                        </li>
                        <li className={s.profileMenuItem}>
                          <Button
                            type="link"
                            onClick={handleGoToPricing}
                            className={`${s.menuItem} ${
                              location.pathname === '/pricing' ? s.active : ''
                            }`}>
                            <EuroCircleOutlined />
                            Pricing
                          </Button>
                        </li>
                      </ul>
                    </div>
                    {initiallyLogged === false && (
                      <div className={s.leftMenu}>
                        <ul style={{ listStyle: 'none' }}>
                          <li className={s.profileMenuItem}>
                            <Button
                              type="link"
                              onClick={handleGoToLogin}
                              className={`${s.menuItem} ${
                                location.pathname === '/buy' ? s.active : ''
                              }`}>
                              <LoginOutlined />
                              Log In
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                  {initiallyLogged && (
                    <div className={s.profileMenuWrapper}>
                      <div className={s.leftMenu}>
                        <ul style={{ listStyle: 'none' }}>
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'userProfile',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                goToProperty();
                              }}>
                              <UploadOutlined />
                              List Property
                            </Button>
                          </li>
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'userProfile',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                handleChangeStep('userProfile');
                                dispatch(setOpenedSider(false));
                              }}>
                              <UserOutlined />
                              User info
                            </Button>
                          </li>
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'favourites',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                handleChangeStep('favourites');
                                dispatch(setOpenedSider(false));
                              }}>
                              <HeartOutlined />
                              Favourites
                            </Button>
                          </li>
                          <Badge count={currentUser?.unreadMessages} offset={[2, 12]}>
                            <li
                              className={cn(s.profileMenuItem, {
                                [s.active]: step === 'messages',
                              })}>
                              <Button
                                className={s.menuItem}
                                type="link"
                                onClick={() => {
                                  handleChangeStep('messages');
                                  dispatch(setOpenedSider(false));
                                }}>
                                <MessageOutlined />
                                Messages
                              </Button>
                            </li>
                          </Badge>
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'properties',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                handleChangeStep('properties');
                                dispatch(setOpenedSider(false));
                              }}>
                              <HomeOutlined />
                              Your Listings
                            </Button>
                          </li>
                          {currentUser?.userType === USER_TYPE_ENUM.DEVELOPER && (
                            <li
                              className={cn(s.profileMenuItem, {
                                [s.active]: step === 'developments',
                              })}>
                              <Button
                                className={s.menuItem}
                                type="link"
                                onClick={() => {
                                  handleChangeStep('developments');
                                  dispatch(setOpenedSider(false));
                                }}>
                                <BookOutlined />
                                Your Developments
                              </Button>
                            </li>
                          )}
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'serviceContacts',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                handleChangeStep('serviceContacts');
                                dispatch(setOpenedSider(false));
                              }}>
                              <ContactsOutlined />
                              Your Services
                            </Button>
                          </li>
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'notifications',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                handleChangeStep('notifications');
                                dispatch(setOpenedSider(false));
                              }}>
                              <NotificationOutlined />
                              Notifications
                            </Button>
                          </li>
                          <li
                            className={cn(s.profileMenuItem, {
                              [s.active]: step === 'subscription',
                            })}>
                            <Button
                              className={s.menuItem}
                              type="link"
                              onClick={() => {
                                handleChangeStep('subscription');
                                dispatch(setOpenedSider(false));
                              }}>
                              <EuroCircleOutlined />
                              Subscription
                            </Button>
                          </li>
                          {currentUser &&
                            currentUser?.roles?.some((role) => role.roleCode === 'ADM') && (
                              <li
                                className={cn(s.profileMenuItem, { [s.active]: step === 'admin' })}>
                                <Button
                                  className={s.menuItem}
                                  type="link"
                                  onClick={() => {
                                    handleChangeStep('admin');
                                  }}>
                                  <SecurityScanOutlined />
                                  Admin Page
                                </Button>
                              </li>
                            )}
                          <li className={s.logoutItem}>
                            <Button className={s.menuItem} type="link" onClick={handleLogout}>
                              <Icon name="logout" size={18} />
                              Log out
                            </Button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </>
            </div>
          </div>
        </ConfigProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
